import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent, Radio } from '@blueprintjs/core';
import moment from 'moment';
import { Button, ButtonSave } from 'components/buttons';
import { FormDateInput, FormSuggest } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { Modal, NotificationInline, NotificationToaster } from 'components/notifications';
import { EmployeeService, WeeklyRestPostponementService } from 'services';
import './Postponements.css';

const defaultArray = [];
const apiDateFormat = "YYYY-MM-DD";
const displayDateFormat = "DD/MM/YYYY";
const driversToShow = 50;

function toApiDate(date) {
    return date ? moment(date).format(apiDateFormat) : null;
}

export function PostponementAddDialog(
    {
        isOpen = false,
        assetId,
        initialDateFrom = null,
        initialDateTo = null,
        onClose,
        onSaved
    }
) {
    const [employeeList, setEmployeeList] = useState(defaultArray);
    const [employeeId, setEmployeeId] = useState(null);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [candidates, setCandidates] = useState(defaultArray);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [candidatesFailed, setCandidatesFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(null);
    const selectedEmployeeId = useRef(null);

    // Both dates are "YYYY-MM-DD", so comparing the strings compares the days.
    const canSave = employeeId != null && dateFrom != null && dateTo != null && dateTo >= dateFrom;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        selectedEmployeeId.current = null;
        setEmployeeList(defaultArray);
        setEmployeeId(null);
        setLoadingCandidates(false);
        setDateFrom(toApiDate(initialDateFrom));
        setDateTo(toApiDate(initialDateTo));
        setCandidates(defaultArray);
        setSelectedCandidateIndex(null);
        setCandidatesFailed(false);
        setErrorMessage("");
    }, [isOpen, initialDateFrom, initialDateTo]);

    function onEmployeeSearch(query) {
        EmployeeService.getEmployeeList(driversToShow, 1, query, "FIRSTNAME", "A", []).then((driverResponse) => {
            setEmployeeList(driverResponse.data.map((driver) => {
                return {
                    id: driver.employeeId,
                    name: `${driver.fullName} - ${driver.location.name}`
                };
            }));
        }, () => {
            NotificationToaster.show(Intent.WARNING, "Could not search for a specific employee, please try again.");
            setEmployeeList(defaultArray);
        });
    }

    function onEmployeeSelect(item) {
        selectedEmployeeId.current = item.id;
        setEmployeeId(item.id);
        setCandidates(defaultArray);
        setSelectedCandidateIndex(null);
        setCandidatesFailed(false);
        setErrorMessage("");

        if (item.id == null) {
            return;
        }

        setLoadingCandidates(true);
        WeeklyRestPostponementService.getCandidates(item.id).then((response) => {
            if (isSelectedEmployee(item.id)) {
                setCandidates(response ?? defaultArray);
            }
        }, () => {
            if (isSelectedEmployee(item.id)) {
                setCandidatesFailed(true);
            }
        }).finally(() => {
            if (isSelectedEmployee(item.id)) {
                setLoadingCandidates(false);
            }
        });
    }

    // A reply can arrive after another driver has been picked; only the current driver's reply is shown.
    function isSelectedEmployee(id) {
        return selectedEmployeeId.current === id;
    }

    function selectCandidate(index) {
        if (saving) {
            return;
        }

        const candidate = candidates[index];
        setSelectedCandidateIndex(index);
        setDateFrom(toApiDate(candidate.dateFrom));
        setDateTo(toApiDate(candidate.dateTo));
    }

    // A date changed by hand no longer matches the selected period, so the selection is cleared to keep
    // the highlighted row in step with what will be saved.
    function onDateFromChange(date) {
        setSelectedCandidateIndex(null);
        setDateFrom(toApiDate(date));
    }

    function onDateToChange(date) {
        setSelectedCandidateIndex(null);
        setDateTo(toApiDate(date));
    }

    function save() {
        setSaving(true);
        setErrorMessage("");

        WeeklyRestPostponementService.createPostponement({ employeeId, assetId: Number(assetId), dateFrom, dateTo }).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Weekly rest postponement recorded.");
            onSaved();
        }, (message) => {
            setErrorMessage(message);
        }).finally(() => {
            setSaving(false);
        });
    }

    const candidateRows = candidates.map((candidate, index) => {
        const dateFromText = moment(candidate.dateFrom).format(displayDateFormat);
        const dateToText = moment(candidate.dateTo).format(displayDateFormat);

        return [
            <Radio
                key={`${candidate.dateFrom}-${candidate.dateTo}`}
                name="postponement-candidate"
                className="postponement-candidate-radio"
                aria-label={`Period from ${dateFromText} to ${dateToText}`}
                checked={selectedCandidateIndex === index}
                onChange={() => selectCandidate(index)}
                disabled={saving}
            />,
            dateFromText,
            dateToText,
            (candidate.countries ?? defaultArray).join(", ")
        ];
    });

    const candidateRowClasses = candidates.map((candidate, index) => {
        return selectedCandidateIndex === index ? "postponement-candidate-selected" : null;
    });

    let candidatesMessage = "";
    if (candidatesFailed) {
        candidatesMessage = "Could not look up the driver's time abroad. Enter the dates manually.";
    } else if (!loadingCandidates && candidates.length === 0) {
        candidatesMessage = "No time abroad found. Enter the dates manually.";
    }

    return (
        <Modal
            title="Record a weekly rest postponement"
            isOpen={isOpen}
            onClose={onClose}
            clickOutsideClose={false}
            updateStateOnClose={false}
            className="postponement-add-dialog"
        >
            <FormSuggest
                id="postponement-driver"
                headingText="Driver"
                items={employeeList}
                selectedValue={employeeId}
                onItemSelect={onEmployeeSelect}
                onFilter={onEmployeeSearch}
                debounceTime={500}
                disabled={saving}
            />

            <ShowHide
                evaluator={employeeId != null}
                show={(
                    <div className="spacer-bottom">
                        <h4>Time abroad in the last 14 weeks</h4>
                        <ShowHide
                            evaluator={candidatesMessage.length > 0}
                            show={(
                                <p id="postponement-candidates-message">{candidatesMessage}</p>
                            )}
                            hide={(
                                <ListingTable
                                    id="postponement-candidates"
                                    headers={["", "From", "To", "Countries"]}
                                    data={candidateRows}
                                    rowClasses={candidateRowClasses}
                                    rowClickEnabled={true}
                                    onRowClick={selectCandidate}
                                    totalRecordCount={candidateRows.length}
                                    loadingData={loadingCandidates}
                                    pageable={false}
                                    showPerPage={false}
                                />
                            )}
                        />
                    </div>
                )}
            />

            <h4>Dates</h4>
            <div className="inline-items">
                <FormDateInput
                    id="postponement-date-from"
                    headingText="From"
                    value={dateFrom}
                    onChange={onDateFromChange}
                    disabled={saving}
                />
                <FormDateInput
                    id="postponement-date-to"
                    headingText="To"
                    value={dateTo}
                    onChange={onDateToChange}
                    disabled={saving}
                />
            </div>

            <ShowHide
                evaluator={errorMessage.length > 0}
                show={(
                    <div className="spacer-top">
                        <NotificationInline
                            show={true}
                            text={errorMessage}
                            intent="danger"
                            allowClose={false}
                        />
                    </div>
                )}
            />

            <div className="inline-items push-right spacer-top">
                <Button text="Cancel" onClick={onClose} disabled={saving} />
                <ButtonSave
                    text="Save"
                    onClick={save}
                    simpleDisabled={!canSave}
                    disabled={saving}
                />
            </div>
        </Modal>
    );
}

PostponementAddDialog.propTypes = {
    isOpen: PropTypes.bool,
    assetId: PropTypes.number.isRequired,
    initialDateFrom: PropTypes.string,
    initialDateTo: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
};
